import React, { useEffect, useState } from 'react';
import { Percent, Check } from 'lucide-react';
import {
  readFinanceDefaults,
  writeFinanceDefaults,
  type FinanceDefaults,
} from '../../lib/financeCatalogs';
import { useTranslation } from '../../lib/i18n';

const FinanceDefaultsSettings: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FinanceDefaults>(() => readFinanceDefaults());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [saved]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    writeFinanceDefaults({
      interestPercentage: form.interestPercentage.trim(),
      opInterest: form.opInterest.trim(),
      overduePenaltyPerDay: (form.overduePenaltyPerDay || '').trim(),
      overduePenaltyPercent: (form.overduePenaltyPercent || '').trim(),
    });
    setSaved(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-zinc-900">
        <Percent className="h-5 w-5 text-emerald-700" aria-hidden />
        <p className="text-sm text-zinc-600">
          {t('settings.financeDesc')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="app-label">{t('settingsForm.defaultInterest')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              className="app-field"
              value={form.interestPercentage}
              onChange={(e) => setForm({ ...form, interestPercentage: e.target.value })}
              placeholder="e.g. 1"
            />
            <p className="mt-1.5 text-xs text-zinc-500">
              On ₹1,00,000, ₹1 per ₹100 = ₹1,000 interest per month. Finance form can switch to daily interest per loan.
            </p>
          </div>
          <div>
            <label className="app-label">{t('settingsForm.opInterest')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              className="app-field"
              value={form.opInterest}
              onChange={(e) => setForm({ ...form, opInterest: e.target.value })}
              placeholder="e.g. 2"
            />
            <p className="mt-1.5 text-xs text-zinc-500">Default opening / out-payment interest value for the form.</p>
          </div>
        </div>

        <div className="border-t border-zinc-100 pt-5">
          <p className="mb-1 text-sm font-semibold text-zinc-800">{t('settingsForm.overduePenalty')}</p>
          <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-4 text-sm text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300">
            <p className="font-mono text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
              Overdue charge = (days late × EMI amount) ÷ 365
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-zinc-500 dark:text-zinc-400">
              <li>Charged separately on each late installment, never on the full loan.</li>
              <li>Days run from the due date until that installment is fully paid (or until today while unpaid).</li>
              <li>Example: EMI ₹5,000 paid 30 days late → 30 × 5,000 ÷ 365 = ₹410.96.</li>
              <li>Hand loans use their own interest rules and are not charged this fee.</li>
              <li>Waivers need an Owner, Admin or Manager and are kept in the audit log.</li>
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-zinc-100 pt-4">
          {saved && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700">
              <Check className="h-4 w-4" /> {t('common.saved')}
            </span>
          )}
          <button type="submit" className="app-btn-primary">
            {t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FinanceDefaultsSettings;
